import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";

/**
 * Privacy — public legal page mounted at /privacy. No auth, no beta gate.
 *
 * STATUS: Working draft. The substantive clauses are written to be
 * standards-aligned with GDPR (EU/UK) + CCPA/CPRA (California) + general
 * US consumer privacy expectations. It still requires sign-off from an
 * actual licensed attorney admitted in our jurisdiction before public
 * launch. See the "For our reviewing attorney" appendix at the bottom for
 * the specific items that need legal verification (governing law, DPA
 * choice, Schrems II cross-border language, age thresholds, etc.).
 */
const ORANGE = "#FF6A00";
const NAVY = "#0F1B2D";

export default function Privacy() {
  return (
    <div className="min-h-screen rm-paper" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Link to="/" className="font-mono-rm uppercase text-[10px] tracking-widest inline-flex items-center gap-1 mb-4"
              style={{ color: "#475569", fontWeight: 700 }}
              data-testid="privacy-back-link">
          <ArrowLeft size={11} /> Back to Resmo
        </Link>

        <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: ORANGE, fontWeight: 700 }}>
          · Resmo · Privacy notice
        </div>
        <h1 className="font-display uppercase leading-tight mt-1"
            style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)", color: NAVY, letterSpacing: "-0.02em" }}
            data-testid="privacy-title">
          We send. We don't sell.
        </h1>
        <p className="text-sm mt-2" style={{ color: "#475569" }}>
          Effective date: <strong>February 1, 2026</strong> · Last updated: <strong>June 4, 2026</strong> ·{" "}
          <Link to="/terms" style={{ color: ORANGE, textDecoration: "underline" }}>See the Terms →</Link>
        </p>

        {/* Working-draft banner */}
        <div className="rm-card-static p-3 mt-4"
             style={{ background: "#FEF3C7", borderColor: "#CA8A04" }}
             data-testid="privacy-draft-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} style={{ color: "#78350F", marginTop: 2, flexShrink: 0 }} />
            <p className="text-[12px] leading-snug" style={{ color: "#78350F" }}>
              <strong>Working draft — pending counsel review.</strong> The substantive clauses below are drafted to align with GDPR (EU/UK), CCPA/CPRA (California), and general US consumer privacy expectations, but they have not yet been reviewed by a licensed attorney in our jurisdiction. They are published in good faith to give beta users a transparent understanding of how we handle data. Material changes will be emailed to subscribers before they take effect.
            </p>
          </div>
        </div>

        <div className="rm-card-static p-4 mt-5"
             style={{ background: "#ECFDF5", borderColor: "#047857", boxShadow: "3px 3px 0 0 #064E3B" }}>
          <div className="flex items-start gap-2">
            <ShieldCheck size={18} style={{ color: "#047857", marginTop: 2, flexShrink: 0 }} />
            <div>
              <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#064E3B", fontWeight: 700 }}>
                · The plain-English version
              </div>
              <ul className="text-[14px] leading-relaxed mt-1.5 space-y-1.5" style={{ color: NAVY }}>
                <li>· Your resume, job descriptions, and tailored outputs are <strong>yours</strong>. We never sell them.</li>
                <li>· We never market your resume to recruiters without your <strong>explicit opt-in</strong> to the Talent Cloud, and even after that you double-opt-in on each intro.</li>
                <li>· We send your text to AI models (Anthropic, Google, OpenAI) to generate tailored content — those API calls are configured for <strong>zero-retention / no-training</strong>.</li>
                <li>· You can <strong>delete your account and every byte of your data</strong> at any time from /account → Delete account.</li>
                <li>· We use Stripe for payments. We never see your card.</li>
              </ul>
            </div>
          </div>
        </div>

        <Section title="1. Who we are (the data controller)" testid="privacy-s1">
          <p>
            Resmo (<em>"Resmo"</em>, <em>"we"</em>, <em>"us"</em>, <em>"our"</em>) is the data controller for the personal data processed through the Resmo service. We are a US-based company operating under the laws of the State of Delaware, with our principal place of business at the address available on request from <strong>privacy@resmo.app</strong>. For EU/UK users, we will appoint an Article 27 representative prior to public launch; the appointment will be published here.
          </p>
        </Section>

        <Section title="2. The categories of personal data we collect" testid="privacy-s2">
          <ul className="list-disc ml-5 space-y-1.5">
            <li><strong>Identifiers</strong>: name, email address, hashed password, IP address, browser/device fingerprint, account ID.</li>
            <li><strong>Resume content</strong>: the base resume you upload + every tailored resume we generate on your behalf. Treated as the most sensitive category in our system; encrypted at rest.</li>
            <li><strong>Job descriptions you submit</strong>: stored alongside your analyses for re-use and to power the Strategy Brief and Playbook features.</li>
            <li><strong>Outcomes you log</strong>: applied / interview / offer / rejection / no-response status per analysis.</li>
            <li><strong>Talent Cloud opt-in metadata</strong>: whether you opted in, your eligibility tier (Bronze/Silver/Gold), the recruiter intros you've sent, accepted, or declined.</li>
            <li><strong>Communications</strong>: emails you send us, support tickets, feedback you submit through the feedback widget.</li>
            <li><strong>Usage telemetry</strong>: pseudonymized page views, feature clicks, billing events (via PostHog, with IP truncation enabled).</li>
            <li><strong>Billing data</strong>: Stripe handles full card data; we only retain the Stripe customer ID, the subscription/credit grant history, and the last four digits of the card for receipt display.</li>
            <li><strong>Inferred data</strong>: skill normalization, role recency, fit scores, and integrity flags derived from the above.</li>
          </ul>
          <p className="mt-2">
            We do <strong>not</strong> intentionally collect special-category data (race, religion, health, sexual orientation, biometric data) and we ask that you do not include it in your resume content. If you do, you authorize us to process it solely for the purposes set out below.
          </p>
        </Section>

        <Section title="3. Why we process it (legal bases)" testid="privacy-s3">
          <p>Under GDPR Article 6, we rely on the following legal bases:</p>
          <ul className="list-disc ml-5 space-y-1.5 mt-2">
            <li><strong>Contract (Art. 6(1)(b))</strong>: to run the toolkit you paid us to run — tailoring, scoring, briefs, playbooks, interviewer research, audio briefings, etc.</li>
            <li><strong>Legitimate interests (Art. 6(1)(f))</strong>: product analytics, fraud prevention, abuse rate-limiting, security logs. We've balanced this against your interests and concluded the impact on you is minimal because telemetry is pseudonymized.</li>
            <li><strong>Consent (Art. 6(1)(a))</strong>: marketing emails outside transactional triggers, the Talent Cloud opt-in, sensitive cross-border transfers, optional cookies. You can withdraw at any time.</li>
            <li><strong>Legal obligation (Art. 6(1)(c))</strong>: tax records, accounting records (we retain payment receipts for 7 years as required by US tax law).</li>
          </ul>
        </Section>

        <Section title="4. AI processors (sub-processors)" testid="privacy-s4">
          <p>
            Tailoring, scoring, Honesty Check, Strategy Brief, and Interview Mode require sending your resume and job-description text to large language models. Each call is configured with the vendor's no-training / zero-retention flag where the API supports it. Current sub-processors:
          </p>
          <ul className="list-disc ml-5 space-y-1.5 mt-2">
            <li><strong>Anthropic</strong> (Claude Sonnet 4.5) — text generation. No-training enabled.</li>
            <li><strong>Google</strong> (Gemini 3 / Nano Banana for image generation) — image + multimodal. No-training enabled.</li>
            <li><strong>OpenAI</strong> (GPT-4o, Whisper, GPT-Image-1) via the Emergent LLM router — text + audio + image. No-training enabled.</li>
            <li><strong>Perplexity</strong> — Interviewer Research feature.</li>
            <li><strong>ElevenLabs</strong> — interview audio briefings.</li>
            <li><strong>Resend</strong> — transactional email delivery.</li>
            <li><strong>Stripe</strong> — payment processing. PCI-DSS Level 1 certified.</li>
            <li><strong>PostHog</strong> — product analytics. IP truncation + cookieless mode where possible.</li>
            <li><strong>MongoDB Atlas</strong> — primary data store. AES-256 at-rest encryption.</li>
            <li><strong>AWS S3</strong> — file storage for resume uploads (encrypted at rest, server-side encryption with AWS-managed keys).</li>
            <li><strong>Adzuna</strong> — job search aggregation (no personal data sent).</li>
          </ul>
          <p className="mt-2">
            The current sub-processor list as of the date above is comprehensive. We will publish updates here at least 30 days before introducing a new sub-processor that touches resume content. If you object to a new sub-processor, you can delete your account before they go live.
          </p>
        </Section>

        <Section title="5. How long we keep it" testid="privacy-s5">
          <p>
            We hold your personal data for as long as your account is active. When you delete your account (from /account → Delete), the deletion runs as a single transaction across all our collections (users, analyses, integrity_checks, interview_playbooks, strategy_briefs, outcomes, talent_cloud_optin, matchmaker_intros, audit_events, and 20+ others) and completes within minutes. We retain billing/tax records for 7 years per US tax law, but they are decoupled from your account data and contain no resume content.
          </p>
          <p className="mt-2">
            Accounts inactive for 24 months are notified by email and archived 30 days later if no response is received. Archived accounts are fully deleted; you can re-sign-up with the same email.
          </p>
        </Section>

        <Section title="6. Sharing & disclosure" testid="privacy-s6">
          <p>We share your data only with:</p>
          <ul className="list-disc ml-5 space-y-1.5 mt-2">
            <li><strong>The sub-processors listed in §4</strong>, each bound by a Data Processing Agreement.</li>
            <li><strong>Recruiters you've explicitly accepted intros from</strong> via the Matchmaker double-opt-in flow.</li>
            <li><strong>Recruiters you've explicitly linked your Resmo account to</strong> via the Resmo Pro Acting-on-behalf feature. You can revoke this link from /account at any time.</li>
            <li><strong>Recipients of public-share links you've personally created</strong> (Strategy Brief share, Playbook share, Resume share). You control whether these exist; you can revoke any of them from your dashboard.</li>
            <li><strong>Successor entities</strong> in the event of a merger, acquisition, or asset sale — but only under the same Privacy notice, and only with 30 days notice to you so you can delete your account first.</li>
            <li><strong>Legal authorities</strong> if compelled by valid legal process (subpoena, search warrant, court order). We will challenge overbroad requests and, where legally permitted, notify you before disclosure.</li>
          </ul>
          <p className="mt-2">
            We do not sell your personal data, and we do not engage in cross-context behavioral advertising. We have no "Do Not Sell My Personal Information" link because there is nothing to sell. If California law eventually requires the link anyway, we will add it.
          </p>
        </Section>

        <Section title="7. International transfers" testid="privacy-s7">
          <p>
            Our primary servers are in the United States. If you access Resmo from the EU/UK, your data will be transferred to the US. We rely on the EU Commission's Standard Contractual Clauses (SCCs, 2021 module-1 controller-to-processor variant) with each sub-processor that handles your personal data outside the EU/EEA/UK, supplemented where appropriate by encryption-in-transit, encryption-at-rest, and contractual no-training commitments. A copy of the SCCs is available from privacy@resmo.app on request.
          </p>
        </Section>

        <Section title="8. Cookies & similar technologies" testid="privacy-s8">
          <p>
            Resmo uses a single first-party HTTP-only cookie (<code>access_token</code>) to keep you signed in. We do not use third-party tracking cookies, advertising cookies, or session replay tools. Our analytics (PostHog) runs in cookieless mode where supported by the browser.
          </p>
        </Section>

        <Section title="9. Your rights" testid="privacy-s9">
          <p>Subject to applicable law, you have the right to:</p>
          <ul className="list-disc ml-5 space-y-1.5 mt-2">
            <li><strong>Access</strong>: ask what data we hold about you.</li>
            <li><strong>Rectify</strong>: correct inaccurate data.</li>
            <li><strong>Erase</strong>: delete your data ("right to be forgotten"). The /account Delete account button is the fastest way.</li>
            <li><strong>Restrict / object</strong>: pause or limit certain processing.</li>
            <li><strong>Data portability</strong>: receive a machine-readable copy of your data. Email privacy@resmo.app and we'll deliver a JSON export within 30 days.</li>
            <li><strong>Withdraw consent</strong>: where processing is based on consent.</li>
            <li><strong>Complain</strong>: to your local data protection authority (in the EU: your national DPA; in the UK: the ICO; in California: the CPPA).</li>
          </ul>
          <p className="mt-2">
            We respond to verifiable requests within 30 days (45 if complex). To submit a request, email <strong>privacy@resmo.app</strong> from the address on your account.
          </p>
        </Section>

        <Section title="10. Children" testid="privacy-s10">
          <p>
            Resmo is not directed at, and we do not knowingly collect personal data from, anyone under the age of 16 (or the applicable minimum age of digital consent in your jurisdiction — in some EU states this is as high as 16, in the US under COPPA it is 13). If you believe a minor has created an account, email privacy@resmo.app and we will delete it within 7 days.
          </p>
        </Section>

        <Section title="11. Security" testid="privacy-s11">
          <p>
            We encrypt sensitive fields (base resumes) at the application layer using AES-256-GCM with keys held in the backend environment and rotated quarterly. All network traffic is over TLS 1.2+. Production access is limited to engineers under a written Information Security Policy. We will publish a SOC 2 Type II report by Q4 2026.
          </p>
          <p className="mt-2">
            We will notify affected users and the relevant supervisory authority within 72 hours of becoming aware of a personal data breach that is likely to result in a risk to your rights and freedoms, as required by GDPR Art. 33.
          </p>
        </Section>

        <Section title="12. Automated decision-making" testid="privacy-s12">
          <p>
            The Resmo toolkit uses AI to generate suggestions, score fit, and flag honesty risks, but every output is delivered to you for review and approval. No decision with legal or similarly significant effects on you is made by the system alone. The Verified by Resmo seal is only issued once you have personally reviewed every flagged claim — there is no automated "approve" path.
          </p>
        </Section>

        <Section title="13. Contact" testid="privacy-s13">
          <p>
            Privacy questions, requests, or complaints: <strong>privacy@resmo.app</strong>. We respond to all inbound requests within 5 business days, and to verifiable rights requests within 30 days.
          </p>
        </Section>

        <Section title="14. Changes to this notice" testid="privacy-s14">
          <p>
            We will notify subscribers by email before any material change to this notice takes effect (at least 30 days in advance). Non-material updates (e.g., adding a vendor for translation services that doesn't touch resume content) will be reflected in the "Last updated" date at the top. Continued use after a material change counts as acceptance.
          </p>
        </Section>

        {/* Reviewer's appendix — visible but minimal */}
        <details className="mt-8 rm-card-static p-3"
                 style={{ background: "#F1F5F9", borderColor: "#94A3B8" }}
                 data-testid="privacy-counsel-appendix">
          <summary className="font-mono-rm uppercase text-[10px] tracking-widest cursor-pointer"
                   style={{ color: "#475569", fontWeight: 700 }}>
            · For our reviewing attorney
          </summary>
          <div className="text-[12px] leading-snug mt-2" style={{ color: NAVY }}>
            <p>Specific items we'd appreciate verification on:</p>
            <ol className="list-decimal ml-5 mt-1 space-y-1">
              <li>Confirm Article 27 EU representative arrangement before public launch.</li>
              <li>Confirm SCCs (2021 module 1) selection vs. UK IDTA preference.</li>
              <li>Confirm 24-month inactive archival window is reasonable for our retention category.</li>
              <li>Confirm the "no-sale" language survives CCPA's expanded "sharing" definition under CPRA.</li>
              <li>Confirm 7-year tax retention is correct for our jurisdiction; some require longer.</li>
              <li>Sub-processor list in §4 — confirm each is bound by a current DPA; flag any that need vendor-side updates.</li>
              <li>Confirm age threshold language (16 default vs. country-specific carve-outs).</li>
              <li>Decide whether to add an arbitration / class-action waiver in the Terms (currently absent).</li>
            </ol>
          </div>
        </details>

        <p className="text-[11px] mt-4" style={{ color: "#94A3B8" }}>
          © 2026 Resmo. Working draft pending counsel review. By using the service you accept this notice.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className="mt-6" data-testid={testid}>
      <h2 className="font-display uppercase text-lg leading-tight mb-2"
          style={{ color: NAVY, letterSpacing: "-0.01em" }}>
        {title}
      </h2>
      <div className="text-[14px] leading-relaxed" style={{ color: NAVY }}>
        {children}
      </div>
    </section>
  );
}
